import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { switchEnterprise } from "../../services/auth";
import {
  createEnterprise,
  getEnterprises,
  getIntegrationHealth,
  getIntegrationSettings,
  getIntegrationEventStatus,
  getRecentIntegrationTasks,
  getAsanaProjects,
  registerAsanaWebhook,
  removeAsanaWebhook,
  triggerIntegrationTest,
  updateEnterpriseSettings,
  updateIntegrationSettings,
} from "../../services/enterprises";
import { useAppDispatch } from "../hooks";
import { login } from "../slices/auth";

export function useCreateEnterprise() {
  const mutation = useMutation({
    mutationKey: ["create-enterprise"],
    mutationFn: createEnterprise,
  });

  return mutation;
}

export function useEnterprises() {
  return useQuery({
    queryKey: ["enterprises"],
    queryFn: getEnterprises,
  });
}

export function useSwitchEnterprise() {
  const dispatch = useAppDispatch();
  return useMutation({
    mutationKey: ["switch-enterprise"],
    mutationFn: switchEnterprise,
    onSuccess: (data) => {
      dispatch(login({ token: data.token }));
    },
  });
}

export function useUpdateEnterpriseSettings() {
  const mutation = useMutation({
    mutationKey: ["update-enterprise-settings"],
    mutationFn: updateEnterpriseSettings,
  });

  return mutation;
}

export function useIntegrationSettings() {
  return useQuery({
    queryKey: ["integration-settings"],
    queryFn: getIntegrationSettings,
  });
}

export function useUpdateIntegrationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-integration-settings"],
    mutationFn: updateIntegrationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-settings"] });
      queryClient.invalidateQueries({ queryKey: ["integration-health"] });
    },
  });
}

export function useIntegrationHealth() {
  return useQuery({
    queryKey: ["integration-health"],
    queryFn: getIntegrationHealth,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useTriggerIntegrationTest() {
  return useMutation({
    mutationKey: ["trigger-integration-test"],
    mutationFn: triggerIntegrationTest,
  });
}

export function useIntegrationEventStatus(
  compositeTaskId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["integration-event-status", compositeTaskId],
    queryFn: () => getIntegrationEventStatus(compositeTaskId!),
    enabled: !!compositeTaskId && options?.enabled !== false,
    refetchInterval: (query) => {
      const data = query.state.data as { state?: string } | undefined;
      if (
        data?.state === "completed" ||
        data?.state === "failed" ||
        data?.state === "not_found"
      ) {
        return false;
      }
      return 5000;
    },
  });
}

export function useRecentIntegrationTasks(platform: string) {
  return useQuery({
    queryKey: ["recent-integration-tasks", platform],
    queryFn: () => getRecentIntegrationTasks(platform),
    staleTime: 30_000,
  });
}

export function useAsanaProjects(enabled = false) {
  return useQuery({
    queryKey: ["asana-projects"],
    queryFn: getAsanaProjects,
    enabled,
    staleTime: 60_000,
  });
}

export function useRegisterAsanaWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["register-asana-webhook"],
    mutationFn: registerAsanaWebhook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-settings"] });
      queryClient.invalidateQueries({ queryKey: ["integration-health"] });
    },
  });
}

export function useRemoveAsanaWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["remove-asana-webhook"],
    mutationFn: removeAsanaWebhook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integration-settings"] });
      queryClient.invalidateQueries({ queryKey: ["integration-health"] });
    },
  });
}
